import { useState, useEffect } from 'react';

import md5 from 'crypto-js/md5';
import { prop, sortBy } from 'ramda';
import { batch, useDispatch, useSelector } from 'react-redux';

import { GENERAL_ERROR_MESSAGE } from 'constants/texts';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { useSnackbar } from 'hooks/useSnackbar';
import i18n from 'i18n';
import { setOriginalBlocksAction, setOriginalTextsAction } from 'store/translate';
import { originalBlocksSelector, originalTextsSelector } from 'store/translate/selectors';
import { translateThunk } from 'store/translate/thunks';
import { capitalize } from 'utils/common';
import logger from 'utils/logger';

import { localeCollection } from './utils';

export const DEFAULT_LOCALE = 'EN-US';

export function useLandingTranslater(props) {
  const { translatableId, translatableType, translationTrigger } = props;

  const dispatch = useDispatch();

  const [value, setValue] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSnackbar = useSnackbar();
  const { isTabletOrMobile } = useMediaBreakpoints();
  const originalBlocks = useSelector(originalBlocksSelector);
  const originalTexts = useSelector(originalTextsSelector);
  const isOriginalBlocksPresent = originalBlocks?.length > 0;
  const showResetButton = isOriginalBlocksPresent;

  const handleReset = () => {
    setValue(null);
    window.location.reload();
  };

  const getScreenType = () => {
    if (!isTabletOrMobile) return 'desktop';
    if (isTabletOrMobile) return 'tablet';
  };

  const handleSubmit = async (enteredlang, readDom) => {
    if (!enteredlang) return;
    const isFirstTranslate = !isOriginalBlocksPresent || readDom;
    if (isFirstTranslate && enteredlang === DEFAULT_LOCALE) {
      onSnackbar('Page successfully translated');
      setOpen(false);
      return;
    }

    const container = document.getElementById('LANDING_PAGES_CONTAINER_ID');
    const dataBlockElems = container.querySelectorAll("[data-block='true']");
    const needTranslate = isFirstTranslate ? Array.from(dataBlockElems).map((elem) => elem.innerHTML) : originalBlocks;
    const needTranslateString = isFirstTranslate
      ? Array.from(dataBlockElems)
          .map((elem) => elem.innerText)
          .toString()
      : originalTexts;
    const sourceDataHash = md5(needTranslateString).toString();
    if (isFirstTranslate) {
      batch(() => {
        dispatch(setOriginalBlocksAction(needTranslate));
        dispatch(setOriginalTextsAction(needTranslateString));
      });
    }
    if (!isFirstTranslate && enteredlang === DEFAULT_LOCALE) {
      dataBlockElems.forEach((elem, i) => {
        elem.innerHTML = `${originalBlocks?.[i]}`;
      });
    }
    translate({ needTranslate: needTranslate, currentBlocks: dataBlockElems, sourceDataHash, targetLang: enteredlang });
  };

  const translate = async ({ needTranslate = [], currentBlocks = [], sourceDataHash, targetLang }) => {
    const params = {
      targetLang,
      translatableType: capitalize(translatableType),
      translatableId,
      sourceDataHash,
      sourceData: needTranslate,
      screenType: getScreenType(),
    };
    setLoading(true);
    onSnackbar(
      i18n.t('proposal_content.translation_widget.in_progress', {
        language: localeCollection.find((locale) => locale.value === targetLang)?.label || 'another language',
      }),
    );

    try {
      const result = await dispatch(translateThunk(params));
      if (result?.status === 200 || result?.status === 201) {
        currentBlocks.forEach((elem, i) => {
          elem.innerHTML = `${result.data?.data?.data?.[i]}`;
        });
        onSnackbar(i18n.t('proposal_content.translation_widget.page_translated'));
        formatTextElems();
        setOpen(false);
      }
    } catch (error) {
      logger.error('TRANSLATE ERROR:', error);
      onSnackbar(GENERAL_ERROR_MESSAGE, true);
    } finally {
      setLoading(false);
    }
  };

  const formatTextElems = () => {
    const container = document.getElementById('LANDING_PAGES_CONTAINER_ID');
    const dataTextElems = container.querySelectorAll("[data-text='true']");
    dataTextElems.forEach((elem) => {
      if (elem.innerText?.length > 0) {
        elem.innerText = `${elem.innerText}  `;
        elem.innerText = elem.innerText.replace(/\s+/g, ' ');
      }
    });
  };

  const sortByAlphabet = sortBy(prop('label'));

  useEffect(() => {
    if (value) {
      setTimeout(() => {
        handleSubmit(value, true);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationTrigger]);

  return {
    loading,
    value,
    setValue,
    open,
    setOpen,
    handleReset,
    showResetButton,
    sortByAlphabet,
    handleSubmit: () => handleSubmit(value),
  };
}

//=====================================
// NOT RELEVANT

// export const useAutoTranslate = (containerId) => {
//   const dispatch = useDispatch();
//   const targetLang = useSelector(targetLangSelector);

//   const fn = async (container) => {
//     if (targetLang === DEFAULT_LOCALE) return;
//     const dataBlockElems = container.querySelectorAll("[data-block='true']");
//     const needTranslate = Array.from(dataBlockElems).map((elem) => elem.innerHTML);

//     try {
//       const result = await dispatch(
//         translateThunk({
//           text: needTranslate,
//           target_lang: targetLang,
//           tag_handling: 'xml',
//           non_splitting_tags: ['span'],
//           split_sentences: 'nonewlines',
//         }),
//       );

//       if (result?.status === 200 || result?.status === 201) {
//         dataBlockElems.forEach((elem, i) => {
//           elem.innerHTML = `${result.data?.translations?.[i]?.text}`;
//         });
//       }
//     } catch (error) {
//       logger.error('TRANSLATE ERROR:', error);
//     }
//   };

//   useEffect(() => {
//     const container = document.getElementById(containerId);
//     if (container) {
//       fn(container);
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [containerId]);
// };
